import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { PageHeader } from "@/components/layout/PageHeader";
import { Stepper } from "@/components/common/Stepper";
import { OrderItemsEditor, OrderItem } from "@/components/orders/OrderItemsEditor";
import { PickupSlotPicker } from "@/components/orders/PickupSlotPicker";
import { OrderSummary } from "@/components/orders/OrderSummary";
import { toast } from "sonner";
import { addHours, parseISO, format } from "date-fns";
import {
  ArrowLeft,
  ArrowRight,
  Scissors,
  Shirt,
  MapPin,
  Check,
  Loader2,
  AlertCircle,
  Plus,
} from "lucide-react";

interface Address {
  id: string;
  line1: string;
  line2?: string;
  city: string;
  state: string;
  pincode: string;
  isDefault?: boolean;
  area: {
    id: string;
    name: string;
    slaCutoffTime: string;
  };
}

interface Slot {
  id: string;
  startAt: string;
  endAt: string;
  capacity: number;
  bookedCount: number;
}

const STEPS = [
  { label: "Order Type", description: "Choose service" },
  { label: "Items", description: "Add garments" },
  { label: "Address", description: "Pickup location" },
  { label: "Schedule", description: "Pick time" },
  { label: "Confirm", description: "Review order" },
];

export default function NewOrderPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [orderType, setOrderType] = useState<"ALTERATION" | "STITCHING">("ALTERATION");
  const [items, setItems] = useState<OrderItem[]>([
    { id: "initial", garmentType: "", qty: 1, notes: "" },
  ]);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<string>("");
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [hasMeasurements, setHasMeasurements] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      const [addressRes, measurementRes] = await Promise.all([
        fetch("/api/customer/addresses"),
        fetch("/api/customer/measurements"),
      ]);

      if (addressRes.ok) {
        const data = await addressRes.json();
        setAddresses(data.addresses || []);
        const defaultAddr = data.addresses?.find((a: Address) => a.isDefault);
        if (defaultAddr) {
          setSelectedAddressId(defaultAddr.id);
        } else if (data.addresses?.length > 0) {
          setSelectedAddressId(data.addresses[0].id);
        }
      }

      if (measurementRes.ok) {
        const data = await measurementRes.json();
        setHasMeasurements(!!data.activeVersion);
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }

  const selectedAddress = addresses.find((a) => a.id === selectedAddressId);
  const cutoffTime = selectedAddress?.area.slaCutoffTime || "14:00";

  const cutoffEligible = selectedSlot
    ? format(parseISO(selectedSlot.startAt), "HH:mm") < cutoffTime
    : false;

  const promisedDeliveryAt = selectedSlot
    ? cutoffEligible
      ? addHours(parseISO(selectedSlot.startAt), 24)
      : addHours(parseISO(selectedSlot.startAt), 48)
    : null;

  function canProceed(): boolean {
    switch (currentStep) {
      case 0:
        if (orderType === "STITCHING" && !hasMeasurements) return false;
        return true;
      case 1:
        return items.every((item) => item.garmentType && item.qty > 0);
      case 2:
        return !!selectedAddressId;
      case 3:
        return !!selectedSlotId;
      case 4:
        return true;
      default:
        return false;
    }
  }

  function handleNext() {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  }

  function handleBack() {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }

  async function handleSubmit() {
    if (!selectedAddressId || !selectedSlotId) return;

    setSubmitting(true);
    try {
      const response = await fetch("/api/customer/orders", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          orderType,
          addressId: selectedAddressId,
          items: items.map((item) => ({
            garmentType: item.garmentType,
            qty: item.qty,
            notes: item.notes,
          })),
          pickupSlotId: selectedSlotId,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to create order");
      }

      const data = await response.json();
      toast.success("Order created successfully!");
      navigate(`/customer/orders/${data.order.id}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to create order");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="New Order" />
        <Skeleton className="h-16" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Create New Order"
        actions={
          <Button
            variant="outline"
            onClick={() => navigate("/customer/orders")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Orders
          </Button>
        }
      />

      <Stepper steps={STEPS} currentStep={currentStep} />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Card>
            <CardContent className="p-6">
              {currentStep === 0 && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-2">
                      What would you like to do?
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Choose the type of service you need
                    </p>
                  </div>

                  <RadioGroup
                    value={orderType}
                    onValueChange={(value) =>
                      setOrderType(value as "ALTERATION" | "STITCHING")
                    }
                    className="grid gap-4 sm:grid-cols-2"
                  >
                    <Label
                      htmlFor="alteration"
                      className={`flex items-start gap-4 p-4 border rounded-lg cursor-pointer transition-colors ${
                        orderType === "ALTERATION"
                          ? "border-primary bg-primary/5"
                          : "hover:bg-muted/50"
                      }`}
                    >
                      <RadioGroupItem value="ALTERATION" id="alteration" className="mt-1" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Scissors className="h-5 w-5 text-primary" />
                          <span className="font-medium">Alteration</span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Modify existing garments - hem, resize, repair, etc.
                        </p>
                      </div>
                    </Label>

                    <Label
                      htmlFor="stitching"
                      className={`flex items-start gap-4 p-4 border rounded-lg cursor-pointer transition-colors ${
                        orderType === "STITCHING"
                          ? "border-primary bg-primary/5"
                          : "hover:bg-muted/50"
                      } ${!hasMeasurements ? "opacity-60" : ""}`}
                    >
                      <RadioGroupItem
                        value="STITCHING"
                        id="stitching"
                        className="mt-1"
                        disabled={!hasMeasurements}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Shirt className="h-5 w-5 text-primary" />
                          <span className="font-medium">Stitching</span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Get new garments stitched from fabric
                        </p>
                        {!hasMeasurements && (
                          <p className="text-xs text-amber-600 mt-2">
                            Requires active measurements
                          </p>
                        )}
                      </div>
                    </Label>
                  </RadioGroup>

                  {orderType === "STITCHING" && !hasMeasurements && (
                    <Alert className="border-amber-300 bg-amber-50">
                      <AlertCircle className="h-4 w-4 text-amber-600" />
                      <AlertDescription className="text-amber-700">
                        You need to complete a measurement appointment before placing
                        a stitching order.{" "}
                        <Button
                          variant="link"
                          className="p-0 h-auto text-amber-700 underline"
                          onClick={() => navigate("/customer/measurements/book")}
                        >
                          Book measurement appointment
                        </Button>
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}

              {currentStep === 1 && (
                <OrderItemsEditor
                  items={items}
                  onChange={setItems}
                  orderType={orderType}
                />
              )}

              {currentStep === 2 && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-2">
                      Select Pickup Address
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Choose where we should pick up your garments
                    </p>
                  </div>

                  {addresses.length > 0 ? (
                    <RadioGroup
                      value={selectedAddressId}
                      onValueChange={setSelectedAddressId}
                      className="space-y-3"
                    >
                      {addresses.map((address) => (
                        <Label
                          key={address.id}
                          htmlFor={address.id}
                          className={`flex items-start gap-4 p-4 border rounded-lg cursor-pointer transition-colors ${
                            selectedAddressId === address.id
                              ? "border-primary bg-primary/5"
                              : "hover:bg-muted/50"
                          }`}
                        >
                          <RadioGroupItem value={address.id} id={address.id} className="mt-1" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <MapPin className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium">{address.line1}</span>
                              {address.isDefault && (
                                <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                                  Default
                                </span>
                              )}
                            </div>
                            {address.line2 && (
                              <p className="text-sm text-muted-foreground">
                                {address.line2}
                              </p>
                            )}
                            <p className="text-sm text-muted-foreground">
                              {address.city}, {address.state} - {address.pincode}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Area: {address.area.name}
                            </p>
                          </div>
                        </Label>
                      ))}
                    </RadioGroup>
                  ) : (
                    <Card>
                      <CardContent className="py-8 text-center">
                        <MapPin className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                        <p className="text-sm text-muted-foreground mb-4">
                          No addresses found. Please add an address first.
                        </p>
                        <Button
                          variant="outline"
                          onClick={() => navigate("/customer/settings")}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Address
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              {currentStep === 3 && selectedAddress && (
                <PickupSlotPicker
                  areaId={selectedAddress.area.id}
                  selectedSlotId={selectedSlotId}
                  onSelect={(slotId, slot) => {
                    setSelectedSlotId(slotId);
                    setSelectedSlot(slot);
                  }}
                  cutoffTime={cutoffTime}
                />
              )}

              {currentStep === 4 && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-2">
                      Review Your Order
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Please review the details before confirming
                    </p>
                  </div>

                  <Alert className="border-emerald-300 bg-emerald-50">
                    <Check className="h-4 w-4 text-emerald-600" />
                    <AlertDescription className="text-emerald-700">
                      Everything looks good! Click "Create Order" to proceed.
                    </AlertDescription>
                  </Alert>
                </div>
              )}

              <div className="flex items-center justify-between mt-8 pt-6 border-t">
                <Button
                  variant="outline"
                  onClick={handleBack}
                  disabled={currentStep === 0}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>

                {currentStep < STEPS.length - 1 ? (
                  <Button onClick={handleNext} disabled={!canProceed()}>
                    Continue
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                ) : (
                  <Button onClick={handleSubmit} disabled={submitting || !canProceed()}>
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Check className="h-4 w-4 mr-2" />
                        Create Order
                      </>
                    )}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <div>
          <OrderSummary
            orderType={orderType}
            items={items}
            address={selectedAddress || null}
            pickupSlot={selectedSlot}
            cutoffEligible={cutoffEligible}
            promisedDeliveryAt={promisedDeliveryAt?.toISOString() || null}
          />
        </div>
      </div>
    </div>
  );
}
